import { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getAverageScore as getAverageScoreHelper } from '../../../../helpers/statistic';
import { SESSION_TYPES } from '../../../../models/session';
import styles from './styles.module.scss';

export const NUM_OF_EVALUATIONS_WHEN_DECIDED = {
  PRESELECTION: 3,
  FINAL_JURY: 1,
};

export const filterFullyDecided = (submissions, sessionType) => {
  return submissions.filter(
    (submission) =>
      submission?.numDecidedEvaluations >=
      NUM_OF_EVALUATIONS_WHEN_DECIDED[sessionType]
  );
};

const filterPartiallyDecided = (submissions, sessionType) => {
  return submissions.filter(
    (submission) =>
      submission?.numDecidedEvaluations > 0 &&
      submission?.numDecidedEvaluations <
        NUM_OF_EVALUATIONS_WHEN_DECIDED[sessionType]
  );
};

const filterNotDecided = (submissions) => {
  return submissions.filter(
    (submission) => submission?.numDecidedEvaluations === 0
  );
};

export const getAverageScore = (submissions, sessionType) => {
  return getAverageScoreHelper(
    filterFullyDecided(submissions, sessionType).length,
    filterFullyDecided(submissions, sessionType).reduce(
      (acc, submission) => acc + submission?.finalPoints,
      0
    )
  );
};

const Stats = ({ submissions, session }) => {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    if (!submissions?.length || !session?.sessionType) return null;

    const regularSubmissions = submissions.filter(
      (submission) => !submission?.isCooperation && !submission?.isWildcard
    );
    const cooperations = submissions.filter(
      (submission) => submission?.isCooperation
    );
    const wildcards = submissions.filter(
      (submission) => submission?.isWildcard
    );

    return {
      submissions: {
        totalSubmissions: {
          key: 'totalSubmissions',
          value: submissions.length,
          label: t('scoreMonitor.chartView.stats.submissions.totalSubmissions'),
        },
        fullyDecided: {
          key: 'fullyDecided',
          value: filterFullyDecided(submissions, session?.sessionType).length,
          label: t('scoreMonitor.chartView.stats.submissions.fullyDecided'),
        },
        partiallyDecided: {
          key: 'partiallyDecided',
          value: filterPartiallyDecided(submissions, session?.sessionType)
            .length,
          label: t('scoreMonitor.chartView.stats.submissions.partiallyDecided'),
        },
        notDecided: {
          key: 'notDecided',
          value: filterNotDecided(submissions).length,
          label: t('scoreMonitor.chartView.stats.submissions.notDecided'),
        },
        averageScore: {
          key: 'averageScore',
          value: getAverageScore(submissions, session?.sessionType),
          label: t('scoreMonitor.chartView.stats.submissions.averageScore'),
        },
      },
      regular: {
        total: {
          key: 'total',
          value: regularSubmissions.length,
          label: t('scoreMonitor.chartView.stats.regular.total'),
        },
        decided: {
          key: 'decided',
          value: filterFullyDecided(regularSubmissions, session?.sessionType)
            .length,
          label: t('scoreMonitor.chartView.stats.regular.decided'),
        },
        undecided: {
          key: 'undecided',
          value: filterNotDecided(regularSubmissions).length,
          label: t('scoreMonitor.chartView.stats.regular.undecided'),
        },
        averageScore: {
          key: 'averageScore',
          value: getAverageScore(regularSubmissions, session?.sessionType),
          label: t('scoreMonitor.chartView.stats.regular.averageScore'),
        },
      },
      cooperations: {
        total: {
          key: 'total',
          value: cooperations.length,
          label: t('scoreMonitor.chartView.stats.cooperations.total'),
        },
        decided: {
          key: 'decided',
          value: filterFullyDecided(cooperations, session?.sessionType).length,
          label: t('scoreMonitor.chartView.stats.cooperations.decided'),
        },
        averageScore: {
          key: 'averageScore',
          value: getAverageScore(cooperations, session?.sessionType),
          label: t('scoreMonitor.chartView.stats.cooperations.averageScore'),
        },
      },
      wildcards: {
        total: {
          key: 'total',
          value: wildcards.length,
          label: t('scoreMonitor.chartView.stats.wildcards.total'),
        },
        decided: {
          key: 'decided',
          value: filterFullyDecided(wildcards, session?.sessionType).length,
          label: t('scoreMonitor.chartView.stats.wildcards.decided'),
        },
        averageScore: {
          key: 'averageScore',
          value: getAverageScore(wildcards, session?.sessionType),
          label: t('scoreMonitor.chartView.stats.wildcards.averageScore'),
        },
      },
    };
  }, [submissions, session]);

  if (!stats) return <div>Processing stats...</div>;

  return (
    <Container className={'my-4, mt-5'}>
      <Row className={'my-4'}>
        <Col xs={12} sm={12} md={12} lg={12}>
          <div className={styles.statContainer}>
            <div className={styles.statContainerTitle}>
              {t('scoreMonitor.chartView.stats.submissions.title')}
            </div>
            <div className={styles.statContainerContent}>
              {Object.values(stats?.submissions).map((stat) => (
                <div className={styles.statContainerItem} key={stat.key}>
                  <span className={styles.label}>{stat.label}</span>
                  <span className={styles.value}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
      {session?.sessionType === SESSION_TYPES.FINAL_JURY && (
        <Row className={'my-4'}>
          <Col xs={12} sm={12} md={4} lg={4}>
            <div className={styles.statContainer}>
              <div className={styles.statContainerTitle}>
                <span>{t('scoreMonitor.chartView.stats.regular.title')}</span>
              </div>
              <div className={styles.statContainerContent}>
                {Object.values(stats?.regular).map((stat) => (
                  <div className={styles.statContainerItem} key={stat.key}>
                    <span className={styles.label}>{stat.label}</span>
                    <span className={styles.value}>{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6} md={4} lg={4}>
            <div className={styles.statContainer}>
              <div className={styles.statContainerTitle}>
                <span>
                  {t('scoreMonitor.chartView.stats.cooperations.title')}
                </span>
              </div>
              <div className={styles.statContainerContent}>
                {Object.values(stats?.cooperations).map((stat) => (
                  <div className={styles.statContainerItem} key={stat.key}>
                    <span className={styles.label}>{stat.label}</span>
                    <span className={styles.value}>{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col xs={12} sm={6} md={4} lg={4}>
            <div className={styles.statContainer}>
              <div className={styles.statContainerTitle}>
                <span>{t('scoreMonitor.chartView.stats.wildcards.title')}</span>
              </div>
              <div className={styles.statContainerContent}>
                {Object.values(stats?.wildcards).map((stat) => (
                  <div className={styles.statContainerItem} key={stat.key}>
                    <span className={styles.label}>{stat.label}</span>
                    <span className={styles.value}>{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Stats;
